// UI strings of Voice Dock, the one recorder of the word sheet and the form recordings (2026-10-13,
// app/src/recordings/dock/; the direction docs/design/directions-2026-10-13/aanitys-voice-dock.txt, whose
// prototype's Finnish is the spec). Finnish (default) and English side by side. Style: docs/FINNISH-STYLE.md
// (no-break space before a unit: "1,3 s").
import { defineArea } from '../area';

const NB = ' ';
const fiS = (x: number) => `${x.toFixed(1).replace('.', ',')}${NB}s`;
const enS = (x: number) => `${x.toFixed(1)}${NB}s`;
const fiS2 = (x: number) => `${x.toFixed(2).replace('.', ',')}${NB}s`;
const enS2 = (x: number) => `${x.toFixed(2)}${NB}s`;
/** A name in the allative (Roosa → Roosalle, Axel → Axelille; FINNISH-STYLE.md "Nimien taivutus"). */
const fiAll = (name: string) => (/[aeiouyäö]$/i.test(name) ? `${name}lle` : `${name}ille`);

export const voiceDockFi = {
  label: 'Äänitys',
  peterson: 'Peterson',
  you: 'Sinä',
  seconds: fiS,
  seconds2: fiS2,
  tempo: 'Petersonin tempo',
  tempoValue: (x: number) => `${String(x).replace('.', ',')}×`,
  playPeterson: 'Kuuntele Peterson',
  playTake: 'Kuuntele otto',
  playSaved: 'Kuuntele oma ottosi',
  playNamed: (name: string) => `Kuuntele: ${name}`,
  stopPlaying: 'Pysäytä',
  seq: 'Peräkkäin',
  seqName: 'Peterson ja sinä peräkkäin',
  yourTurn: 'Sinun vuorosi',
  recTime: (x: number, max: number) => `${fiS(x)} / ${max}${NB}s`,
  shadowLine: 'Peterson, sitten sinä',
  unsaved: 'Tallentamaton otto',
  silent: 'Ääntä ei kuulunut. Puhu lähempänä mikrofonia ja äänitä uudelleen.',
  trimAuto: (kept: number, whole: number) => `${fiS(kept)} · leikattu automaattisesti (alkuperäinen ${fiS(whole)})`,
  trimHand: (kept: number, whole: number) => `${fiS(kept)} · leikattu käsin (alkuperäinen ${fiS(whole)})`,
  analysis: 'Pituudet ja paino',
  resetAuto: 'Palauta automaattinen',
  handleIn: 'Oton alku',
  handleOut: 'Oton loppu',
  deleteTake: 'Poista otto',
  deleted: 'Otto poistettu',
  undo: 'Kumoa',
  edit: 'Muokkaa…',
  shadow: 'Varjostus',
  record: 'Äänitä',
  again: 'Uusi otto',
  stop: 'Lopeta',
  tapToStop: 'Napauta lopettaaksesi',
  releaseToStop: 'Päästä irti lopettaaksesi',
  done: 'Valmis',
  saving: 'Tallennetaan…',
  saved: 'Tallennettu',
  savedShared: (name: string) => `Tallennettu · ${name} kuulee tämän`,
  savedQueued: (name: string) => `Tallennettu · lähtee ${fiAll(name)}, kun yhteys palaa`,
  savedQueuedSolo: 'Tallennettu · lähtee, kun yhteys palaa',
  denied: 'Mikrofoni on estetty. Salli se: Safari → aA → Sivuston asetukset → Mikrofoni.',
  retry: 'Yritä uudelleen',
  word: 'Sana',
  form: 'Muoto',
};

export type VoiceDockMessages = typeof voiceDockFi;

const enAll = (name: string) => name;

export const voiceDockEn: VoiceDockMessages = {
  label: 'Recording',
  peterson: 'Peterson',
  you: 'You',
  seconds: enS,
  seconds2: enS2,
  tempo: 'Peterson’s tempo',
  tempoValue: (x) => `${x}×`,
  playPeterson: 'Play Peterson',
  playTake: 'Play the take',
  playSaved: 'Play your take',
  playNamed: (name) => `Play: ${name}`,
  stopPlaying: 'Stop',
  seq: 'In turn',
  seqName: 'Peterson, then you',
  yourTurn: 'Your turn',
  recTime: (x, max) => `${enS(x)} / ${max}${NB}s`,
  shadowLine: 'Peterson, then you',
  unsaved: 'Unsaved take',
  silent: 'No sound was heard. Speak closer to the microphone and record again.',
  trimAuto: (kept, whole) => `${enS(kept)} · trimmed automatically (original ${enS(whole)})`,
  trimHand: (kept, whole) => `${enS(kept)} · trimmed by hand (original ${enS(whole)})`,
  analysis: 'Lengths and stress',
  resetAuto: 'Back to the automatic cut',
  handleIn: 'Start of the take',
  handleOut: 'End of the take',
  deleteTake: 'Delete the take',
  deleted: 'Take deleted',
  undo: 'Undo',
  edit: 'Edit…',
  shadow: 'Shadowing',
  record: 'Record',
  again: 'New take',
  stop: 'Stop',
  tapToStop: 'Tap to stop',
  releaseToStop: 'Release to stop',
  done: 'Done',
  saving: 'Saving…',
  saved: 'Saved',
  savedShared: (name) => `Saved · ${name} can hear it`,
  savedQueued: (name) => `Saved · goes to ${enAll(name)} when the connection is back`,
  savedQueuedSolo: 'Saved · goes when the connection is back',
  denied: 'The microphone is blocked. Allow it: Safari → aA → Website Settings → Microphone.',
  retry: 'Try again',
  word: 'Word',
  form: 'Form',
};

export const area = defineArea('voiceDock', voiceDockFi, voiceDockEn);

declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof voiceDockFi> {}
}
